import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { activitiesAPI, resolveMediaUrl } from '../services/api';
import ErrorMessage from './ErrorMessage';
import LoadingSpinner from './LoadingSpinner';
import { useSelectedActivities } from './SelectedActivitiesContext';
import { useDate } from './DateContext';

const Activities = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedActivityDetail, setSelectedActivityDetail] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [viewMode, setViewMode] = useState('landscape'); // 'landscape' or 'portrait'
  const [selectedImageIndex, setSelectedImageIndex] = useState({}); // Track selected image for each activity
  const { selectedActivities, toggleActivity, setSelectedActivities, isActivitySelected } = useSelectedActivities();
  const { checkIn, checkOut, formatDate } = useDate();

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      setError('');
      const activitiesResponse = await activitiesAPI.getAll();
      console.log('Activities API Response:', activitiesResponse);
      console.log('Activities Data:', activitiesResponse.data);
      // Handle both array and object response
      const activities = Array.isArray(activitiesResponse.data) 
        ? activitiesResponse.data 
        : (activitiesResponse.data?.activities || []);
      console.log('Set activities data:', activities);
      setActivities(activities);
    } catch (error) {
      console.error('Error fetching activities:', error);
      setError('Unable to load activities. Please check your connection and try again.');
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const handleActivityToggle = (activityId, e) => {
    e?.stopPropagation();
    toggleActivity(activityId);
  };

  const handleViewDetails = (activity) => {
    setSelectedActivityDetail(activity);
    setShowDetailModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedActivityDetail(null);
  };

  const handleSelectAllActivities = () => {
    if (selectedActivities.length === activities.length) {
      setSelectedActivities([]);
    } else {
      setSelectedActivities(activities.map(activity => activity._id));
    }
  };

  const handleBookActivity = () => {
    if (selectedActivities.length === 0) {
      alert('Please select at least one activity to book.');
      return;
    }

    const phoneNumber = '9459457733'; // WhatsApp number (945 945 7733)
    
    // Build message with selected activity details
    let message = 'Hello, I would like to book the following activities:\n\n';
    if (checkIn && checkOut) {
      message += `📅 Check-in: ${formatDate(checkIn)}\n`;
      message += `📅 Check-out: ${formatDate(checkOut)}\n\n`;
    }
    
    let totalAmount = 0;
    selectedActivities.forEach((activityId, index) => {
      const activity = activities.find(a => String(a._id) === String(activityId));
      if (activity) {
        message += `${index + 1}. ${activity.activityName}\n`;
        message += `   Location: ${activity.location}\n`;
        message += `   Price: ₹${activity.costing}\n`;
        if (activity.inclusion) {
          message += `   Inclusions: ${activity.inclusion}\n`;
        }
        message += '\n';
        totalAmount += activity.costing || 0;
      }
    });
    
    message += `Total Amount: ₹${totalAmount}\n\n`;
    message += 'Please confirm the booking. Thank you!';

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  if (loading) {
    return (
      <section className="section" id="activities">
        <div className="container">
          <h2 className="section-title">Activities</h2>
          <LoadingSpinner text="Loading activities..." />
        </div>
      </section>
    );
  }

  return (
    <section className="section" id="activities">
      <Helmet>
        <title>Activities in Goa | GoaDeals</title>
        <meta name="description" content="Discover and book activities in Goa – water sports, tours, adventures and more. Select activities and book via WhatsApp." />
      </Helmet>
      <div className="container">
        <h2 className="section-title">Activities</h2>

        {error && (
          <ErrorMessage 
            message={error} 
            type="error" 
            onClose={() => setError('')}
          />
        )}

        <div className="activities-section">
          <div className="section-header">
            <h3>Available Activities</h3>
            <div className="section-header-actions">
              <div className="view-mode-toggle">
                <button 
                  className={`view-mode-btn ${viewMode === 'landscape' ? 'active' : ''}`}
                  onClick={() => setViewMode('landscape')}
                  title="Landscape View"
                >
                  <i className="fas fa-th-large"></i> Landscape
                </button>
                <button 
                  className={`view-mode-btn ${viewMode === 'portrait' ? 'active' : ''}`}
                  onClick={() => setViewMode('portrait')}
                  title="Portrait View"
                >
                  <i className="fas fa-th"></i> Portrait
                </button>
              </div>
              <button 
                className="select-all-btn"
                onClick={handleSelectAllActivities}
              >
                {selectedActivities.length === activities.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          </div>

          <div className={`activities-grid ${viewMode}`}>
            {activities.length === 0 && !loading ? (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px' }}>
                <p>No activities available at the moment.</p>
              </div>
            ) : (
              activities.map((activity) => {
                // Process all images
                const processImageUrl = (img) => {
                  if (!img) return 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&h=600&fit=crop';
                  return resolveMediaUrl(img);
                };

                const allImages = activity.images && activity.images.length > 0 
                  ? activity.images.map(processImageUrl)
                  : ['https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&h=600&fit=crop'];
                
                const currentImageIndex = selectedImageIndex[activity._id] || 0;
                const activityImage = allImages[currentImageIndex];
                
                // Get rating - use from activity or default to 4
                const rating = activity.rating || 4;
                const totalReviews = activity.totalReviews || activity.reviews || 0;
                
                // Parse inclusions and exclusions
                const inclusions = activity.inclusion ? activity.inclusion.split(',').map(item => item.trim()).filter(item => item.length > 0) : [];
                const exclusions = activity.exclusion ? activity.exclusion.split(',').map(item => item.trim()).filter(item => item.length > 0) : [];
                
                return (
                  <div key={activity._id} className={`activity-card-new ${viewMode}`}>
                    <div className={`activity-card-content ${viewMode}`}>
                      {/* Checkbox for Selection */}
                      <div className="activity-card-checkbox-wrapper">
                        <input
                          type="checkbox"
                          id={`activity-checkbox-${activity._id}`}
                          checked={isActivitySelected(activity._id)}
                          onChange={(e) => handleActivityToggle(activity._id, e)}
                          className="activity-card-checkbox"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <label 
                          htmlFor={`activity-checkbox-${activity._id}`}
                          className="activity-card-checkbox-label"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Select
                        </label>
                      </div>

                      {/* Image Section - Show all images with navigation */}
                      <div className="activity-image-section">
                        <div className="activity-main-image-container">
                          <img 
                            src={activityImage} 
                            alt={activity.activityName}
                            className="activity-main-image"
                            onError={(e) => {
                              e.target.src = 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&h=600&fit=crop';
                            }}
                          />
                          {allImages.length > 1 && (
                            <>
                              <button 
                                className="activity-image-nav-btn prev"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedImageIndex({
                                    ...selectedImageIndex,
                                    [activity._id]: (currentImageIndex - 1 + allImages.length) % allImages.length
                                  });
                                }}
                                aria-label="Previous image"
                              >
                                <i className="fas fa-chevron-left"></i>
                              </button>
                              <button 
                                className="activity-image-nav-btn next"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedImageIndex({
                                    ...selectedImageIndex,
                                    [activity._id]: (currentImageIndex + 1) % allImages.length
                                  });
                                }}
                                aria-label="Next image"
                              >
                                <i className="fas fa-chevron-right"></i>
                              </button>
                              <div className="activity-image-count-badge">
                                <span>{currentImageIndex + 1} / {allImages.length}</span>
                              </div>
                            </>
                          )}
                        </div>
                        {/* Image Thumbnails */}
                        {allImages.length > 1 && (
                          <div className="activity-image-thumbnails">
                            {allImages.map((img, idx) => (
                              <img
                                key={idx}
                                src={img}
                                alt={`${activity.activityName} - Image ${idx + 1}`}
                                className={`activity-thumbnail ${currentImageIndex === idx ? 'active' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedImageIndex({
                                    ...selectedImageIndex,
                                    [activity._id]: idx
                                  });
                                }}
                                onError={(e) => {
                                  e.target.src = 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&h=600&fit=crop';
                                }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Content Section */}
                      <div className="activity-details-section">
                        <div className="activity-header-new">
                          <h3 className="activity-title-new">{activity.activityName}</h3>
                          <div className="activity-price-badge">
                            ₹{activity.costing} PER PERSON
                          </div>
                        </div>
                        
                        {/* Reviews */}
                        <div className="activity-reviews">
                          <div className="activity-stars">
                            {[...Array(5)].map((_, i) => (
                              <span key={i} className={`star ${i < Math.floor(rating) ? 'filled' : ''}`}>
                                ★
                              </span>
                            ))}
                          </div>
                          <span className="activity-review-count">{totalReviews} customer reviews</span>
                        </div>
                        
                        {/* Description */}
                        {activity.description && (
                          <p className="activity-description-new">
                            {activity.description.length > 200 
                              ? `${activity.description.substring(0, 200)}...` 
                              : activity.description}
                          </p>
                        )}
                        
                        {/* Inclusions */}
                        {inclusions.length > 0 && (
                          <div className="activity-inclusions-section">
                            <h5 className="activity-section-title">Inclusions:</h5>
                            <ul className="activity-inclusions-list">
                              {inclusions.map((item, index) => (
                                <li key={index}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Exclusions */}
                        {exclusions.length > 0 && (
                          <div className="activity-exclusions-section">
                            <h5 className="activity-section-title">Exclusions:</h5>
                            <ul className="activity-exclusions-list">
                              {exclusions.map((item, index) => (
                                <li key={index}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Action Buttons */}
                        <div className="activity-actions">
                          <button 
                            className="btn-view-details"
                            onClick={() => handleViewDetails(activity)}
                          >
                            View Details
                          </button>
                          <button 
                            className="btn-enquire-now"
                            onClick={() => {
                              const phoneNumber = '9459457733';
                              const message = `Hello, I would like to enquire about: ${activity.activityName}\nLocation: ${activity.location}\nPrice: ₹${activity.costing}`;
                              const encodedMessage = encodeURIComponent(message);
                              const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
                              window.open(whatsappUrl, '_blank');
                            }}
                          >
                            Enquire Now
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {selectedActivities.length > 0 && (
          <div className="selection-summary">
            <h3>Your Selection Summary</h3>
            <div className="summary-content">
              <div className="selected-activities">
                <h4>Selected Activities ({selectedActivities.length})</h4>
                <div className="selected-list">
                  {selectedActivities.map(activityId => {
                    const activity = activities.find(a => String(a._id) === String(activityId));
                    return (
                      <div key={activityId} className="selected-item">
                        <span>{activity?.activityName}</span>
                        <span className="selected-price">₹{activity?.costing}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="total-summary">
                <div className="grand-total">
                  <strong>
                    Total: ₹{selectedActivities.reduce((total, activityId) => {
                      const activity = activities.find(a => String(a._id) === String(activityId));
                      return total + (activity?.costing || 0);
                    }, 0)}
                  </strong>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Book Activity Button */}
        <div className="book-activity-section" style={{ marginTop: '2rem', textAlign: 'center' }}>
          <button 
            className="btn btn-primary book-activity-btn"
            onClick={handleBookActivity}
            style={{
              padding: '12px 32px',
              fontSize: '1.1rem',
              fontWeight: '600',
              backgroundColor: '#809947',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: selectedActivities.length === 0 ? 'not-allowed' : 'pointer',
              opacity: selectedActivities.length === 0 ? 0.6 : 1,
              transition: 'all 0.3s ease',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
            disabled={selectedActivities.length === 0}
          >
            <i className="fab fa-whatsapp" style={{ fontSize: '1.2rem' }}></i>
            Book Activity
          </button>
          {selectedActivities.length === 0 && (
            <p style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
              Please select at least one activity to book
            </p>
          )}
        </div>
      </div>

      {/* Activity Detail Modal */}
      {showDetailModal && selectedActivityDetail && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="activity-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedActivityDetail.activityName}</h3>
              <button className="close-modal" onClick={handleCloseModal}>×</button>
            </div>
            <div className="modal-body">
              {/* Activity Images */}
              {selectedActivityDetail.images && selectedActivityDetail.images.length > 0 ? (
                <div className="activity-detail-images">
                  {selectedActivityDetail.images.map((img, index) => {
                    const imageUrl = resolveMediaUrl(img);
                    return (
                      <img 
                        key={index} 
                        src={imageUrl} 
                        alt={`${selectedActivityDetail.activityName} - Image ${index + 1}`}
                        className="activity-detail-image"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&h=600&fit=crop';
                        }}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="activity-detail-images">
                  <img 
                    src="https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&h=600&fit=crop"
                    alt={selectedActivityDetail.activityName}
                    className="activity-detail-image"
                  />
                </div>
              )}

              {/* Activity Info */}
              <div className="activity-detail-info">
                {(() => {
                  const modalInclusions = selectedActivityDetail.inclusion 
                    ? selectedActivityDetail.inclusion.split(',').map(item => item.trim()).filter(item => item.length > 0)
                    : [];
                  const modalExclusions = selectedActivityDetail.exclusion 
                    ? selectedActivityDetail.exclusion.split(',').map(item => item.trim()).filter(item => item.length > 0)
                    : [];
                  
                  return (
                    <>
                      <div className="detail-row">
                        <strong>Location:</strong>
                        <span>{selectedActivityDetail.location}</span>
                      </div>
                      
                      <div className="detail-row">
                        <strong>Price:</strong>
                        <span className="detail-price">₹{selectedActivityDetail.costing} per person</span>
                      </div>

                      {selectedActivityDetail.rating && (
                        <div className="detail-row">
                          <strong>Rating:</strong>
                          <div className="activity-reviews">
                            <div className="activity-stars">
                              {[...Array(5)].map((_, i) => (
                                <span key={i} className={`star ${i < Math.floor(selectedActivityDetail.rating || 0) ? 'filled' : ''}`}>
                                  ★
                                </span>
                              ))}
                            </div>
                            <span className="activity-review-count">
                              {selectedActivityDetail.totalReviews || 0} customer reviews
                            </span>
                          </div>
                        </div>
                      )}

                      {selectedActivityDetail.description && (
                        <div className="detail-row">
                          <strong>Description:</strong>
                          <span>{selectedActivityDetail.description}</span>
                        </div>
                      )}

                      {modalInclusions.length > 0 && (
                        <div className="detail-row">
                          <strong>Inclusions:</strong>
                          <ul className="activity-inclusions-list">
                            {modalInclusions.map((item, index) => (
                              <li key={index}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {modalExclusions.length > 0 && (
                        <div className="detail-row">
                          <strong>Exclusions:</strong>
                          <ul className="activity-exclusions-list">
                            {modalExclusions.map((item, index) => (
                              <li key={index}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={handleCloseModal}
              >
                Close
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  handleActivityToggle(selectedActivityDetail._id);
                }}
              >
                {isActivitySelected(selectedActivityDetail._id) ? 'Deselect' : 'Select'} Activity
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  const phoneNumber = '9459457733';
                  const message = `Hello, I would like to enquire about: ${selectedActivityDetail.activityName}\nLocation: ${selectedActivityDetail.location}\nPrice: ₹${selectedActivityDetail.costing}`;
                  const encodedMessage = encodeURIComponent(message);
                  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
                  window.open(whatsappUrl, '_blank');
                }}
              >
                Enquire Now
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Activities;
